import { FC, useMemo, useState } from 'react';
import s from './PaymentPage.module.css';
import { PaymentBankAccount } from './components/PaymentBankAccount/PaymentBankAccount';
import { PaymentCard } from './components/PaymentCard/PaymentCard';
import {
  PaymentMethod,
  PaymentMethodTabs,
} from './components/PaymentMethodTabs/PaymentMethodTabs';
import { PaymentSelect } from './components/PaymentSelect/PaymentSelect';
import { PaymentSummary } from './components/PaymentSummary/PaymentSummary';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetPlansQuery } from 'store/features/payment/payment-api';
import { useGetUserDataQuery } from 'store/features/user/user-api';
import { Loader } from 'components/common/Loader/Loader';
import { RedirectHandler } from 'components/common/RedirectHandler/RedirectHandler';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguages } from 'store/features/settings/selectors';

const PaymentPage: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const languages = useSelector(selectLanguages);

  const langOptions = languages.map((lang) => ({
    id: lang.code,
    value: lang.code === 'hy' ? 'AM' : lang.code.toUpperCase(),
    label: lang.name,
  }));

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');

  const [searchParams] = useSearchParams();
  const planId = searchParams.get('planId');

  const { error, isError, isLoading, isSuccess } = useGetUserDataQuery();

  const { plans } = useGetPlansQuery(undefined, {
    skip: !isSuccess,
    selectFromResult: ({ data }) => ({
      plans: data ?? [],
    }),
  });

  const plan = useMemo(
    () => plans.find((item) => item.planId === planId) ?? plans[0] ?? null,
    [planId, plans],
  );

  const handlePay = () => {
    console.log('pay');
  };

  const handlePlanChange = (nextPlanId: string) => {
    navigate(`/payment?planId=${nextPlanId}`);
  };

  const handleLocaleChange = async (locale: string) => {
    await i18n.changeLanguage(locale);
    window.location.reload();
  };

  const isUnauthorized =
    isError && error && 'status' in error && error.status === 401;
  if (isLoading) return <Loader />;
  if (isUnauthorized) return <RedirectHandler />;

  if (!plan) {
    return <div>{t('payment.planNotFound')}</div>;
  }

  return (
    <div className={s['container']} aria-label='Оплата тарифного плана'>
      <section className={s['payment__content']}>
        <div className={s['payment__top-row']}>
          <PaymentMethodTabs
            value={paymentMethod}
            onChange={setPaymentMethod}
          />

          <div className={s['payment__select-wrp']}>
            <PaymentSelect
              variant='language'
              value={(i18n.resolvedLanguage?.toUpperCase() ?? 'EN').slice(0, 2)}
              options={langOptions}
              onChange={handleLocaleChange}
              ariaLabel={t('payment.locale')}
            />
          </div>
        </div>

        {paymentMethod === 'card' ? <PaymentCard /> : <PaymentBankAccount />}
      </section>

      <PaymentSummary
        plan={plan}
        plans={plans}
        onPlanChange={handlePlanChange}
        onPay={handlePay}
      />
    </div>
  );
};

export default PaymentPage;
